import React, {useState, useEffect, useRef, useCallback} from 'react';
import {ChevronDown, Trash2, Loader} from 'lucide-react';
import CatalogSearchBar from "../CatalogSearchBar";
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {URLS_UPLOADS} from "../../../config/url";
import Notification from "../../Common/Notification";

const BrandsList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // ── URL-persisted state ──────────────────────────────────────────
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    const debouncedSearchTerm = searchParams.get('q') || '';
    // Filtre de statut : '' (tous) | 'true' (actifs) | 'false' (inactifs)
    const activeFilter = ['true', 'false'].includes(searchParams.get('active')) ? searchParams.get('active') : '';

    // Local input state (debounced before writing to URL)
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');

    // Sync input when URL changes externally (e.g. browser Back)
    useEffect(() => {
        setSearchTerm(searchParams.get('q') || '');
    }, [searchParams]);

    const updateParams = useCallback((updates, push = false) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v === '' || v === null || v === undefined) next.delete(k);
                else next.set(k, String(v));
            });
            return next;
        }, {replace: !push});
    }, [setSearchParams]);

    const setCurrentPage = useCallback((page) => {
        updateParams({page}, true);
    }, [updateParams]);

    const [selectedAll, setSelectedAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success', // 'success', 'error', 'loading'
        message: '',
    });
    const queryClient = useQueryClient();
    const selectionBarRef = useRef(null);

    // Add CSS for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideDown {
                from {
                    opacity: 0;
                    transform: translateY(-20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .selection-bar-appear {
                animation: slideDown 0.3s ease-out forwards;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Debounce: write search to URL after 600 ms, reset page to 1
    useEffect(() => {
        const timer = setTimeout(() => {
            updateParams({q: searchTerm || null, page: null});
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    // Apply animation when selection changes
    useEffect(() => {
        if (selectedItems.length > 0 && selectionBarRef.current) {
            selectionBarRef.current.classList.remove('selection-bar-appear');
            // This triggers a reflow, allowing the animation to restart
            void selectionBarRef.current.offsetWidth;
            selectionBarRef.current.classList.add('selection-bar-appear');
        }
    }, [selectedItems.length]);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        // Auto hide after 5 seconds for success notifications
        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // Function to fetch brands from the API with server-side pagination and filtering
    const fetchBrands = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build the query string with pagination and name filter
            let url = `/brands?page=${currentPage}`;
            if (debouncedSearchTerm.trim()) {
                url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            }
            if (activeFilter) {
                url += `&active=${activeFilter}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            // Update totalPages based on the pagination structure
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                return response.data.data;
            }

            // Fallback if response structure is different
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des marques";
            throw new Error(errorMessage);
        }
    };

    // Function to delete brands
    const deleteBrands = async (brandIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            console.log("Deleting brands:", brandIds);

            // Delete brands one by one
            const results = await Promise.all(
                brandIds.map(id =>
                    api.delete(`/brands/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        console.error(`Failed to delete brand ${id}:`, err);
                        return {error: err, id};
                    })
                )
            );

            // Log results for debugging
            console.log("Delete results:", results);

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                console.error("Failed deletions:", failures);
                if (failures.length === brandIds.length) {
                    throw new Error("Impossible de supprimer les marques sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${brandIds.length} marques n'ont pas pu être supprimées`);
                }
            }

            return brandIds;
        } catch (error) {
            console.error("Delete error:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des marques";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteBrands,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries(['brands']);
            showNotification('success', 'Marques supprimées avec succès');
        },
        onError: (error) => {
            console.error("Delete mutation error:", error);
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Use react-query to fetch and cache the data
    const {data: brands, isLoading, error} = useQuery({
        queryKey: ['brands', currentPage, debouncedSearchTerm, activeFilter],
        queryFn: fetchBrands,
        keepPreviousData: true,
    });

    // Use the data directly since filtering happens on the server
    const filteredData = brands || [];

    // Generate pagination buttons array
    const pages = Array.from({length: totalPages}, (_, index) => index + 1);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredData.map(item => item.id) : []);
    };

    // Handle individual item selection
    const handleSelectItem = (brandId, e) => {
        e.stopPropagation();
        setSelectedItems((prevSelected) =>
            prevSelected.includes(brandId)
                ? prevSelected.filter((id) => id !== brandId)
                : [...prevSelected, brandId]
        );
    };

    // Handle deletion of selected brands
    const handleDeleteSelected = () => {
        if (selectedItems.length > 0) {
            setShowConfirmDialog(true);
        }
    };

    // Function to confirm deletion
    const confirmDelete = () => {
        setShowConfirmDialog(false);
        deleteMutation.mutate(selectedItems);
    };

    // Function to cancel deletion
    const cancelDelete = () => {
        setShowConfirmDialog(false);
    };

    // Handle edit brand
    const handleEditBrand = (brand) => {
        console.log('teh url', `/catalogue/brand/edit/${brand.id}`);
        navigate(`/catalogue/brand/edit/${brand.id}`, {state: {returnTo: location.pathname + location.search}});
    };

    // Handle pagination
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    // Show loading state
    if (isLoading) return <div className="flex justify-center items-center h-40">
        <Loader className="w-8 h-8 text-primary animate-spin"/>
    </div>;

    // Show error state
    if (error) return <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>;

    return (
        <div className="max-w-full">
            {/* Search Bar — même style que l'onglet Produits */}
            <CatalogSearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                activeSearch={debouncedSearchTerm}
                status={activeFilter}
                onStatusChange={(value) => updateParams({active: value || null, page: null})}
                onClearAll={() => {
                    setSearchTerm('');
                    updateParams({q: null, active: null, page: null});
                }}
                onClear={() => {
                    setSearchTerm('');
                    updateParams({q: null, page: null});
                }}
            />

            {/* Selected Items Info and Delete Button */}
            {selectedItems.length > 0 && (
                <div
                    ref={selectionBarRef}
                    className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20
                                border-l-4 border-primary shadow-sm selection-bar-appear"
                >
                    <div className="flex items-center">
                        <div className="w-6 h-6 bg-primary flex items-center justify-center rounded-sm mr-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white"
                                 viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"/>
                            </svg>
                        </div>
                        <span
                            className="text-gray-700 dark:text-gray-200">{selectedItems.length} marque{selectedItems.length > 1 ? 's' : ''} sélectionnée{selectedItems.length > 1 ? 's' : ''}</span>
                    </div>
                    <button
                        onClick={handleDeleteSelected}
                        className="flex items-center px-3 py-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-colors duration-200"
                        disabled={deleteMutation.isLoading}
                    >
                        <Trash2 className="w-5 h-5 mr-1.5"/>
                        <span>Supprimer la sélection</span>
                    </button>
                </div>
            )}


            {/* Table Structure */}
            <div className="card overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                        <th className="w-4 px-4 py-3 text-left">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                checked={selectedAll}
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <div className="flex items-center">
                                NOM
                                <ChevronDown className="ml-1 w-4 h-4 text-gray-500 dark:text-gray-400"/>
                            </div>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredData.length === 0 ? (
                        <tr>
                            <td colSpan="2" className="p-4 text-center text-gray-500 dark:text-gray-400">
                                Aucune marque trouvée
                            </td>
                        </tr>
                    ) : (
                        filteredData.map((item) => (
                            <tr
                                key={item.id}
                                className="border-b last:border-b-0 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                onClick={() => handleEditBrand(item)}
                            >
                                <td className="w-4 px-4 py-3">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                        checked={selectedItems.includes(item.id)}
                                        onChange={(e) => handleSelectItem(item.id, e)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center min-w-0">
                                        <div
                                            className="h-10 w-10 mr-3 flex-shrink-0 flex items-center justify-center text-white text-xs font-medium rounded"
                                            style={{backgroundColor: item.color || '#4fb7ad'}}
                                        >
                                            {item.imageUrl && item.imageUrl !== '' ?
                                                <img
                                                    src={item.imageUrl.startsWith('http') ? item.imageUrl : `${URLS_UPLOADS}${item.imageUrl}`}
                                                    alt={item.name}
                                                    className="h-full w-full object-cover rounded"
                                                /> :
                                                item.name.slice(0, 2).toUpperCase()
                                            }
                                        </div>
                                        <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{item.name}</span>
                                    </div>
                                </td>

                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination - Responsive */}
            {totalPages > 1 && (
                <div className="flex justify-center sm:justify-end mt-6 overflow-x-auto">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Previous page button */}
                        <button
                            onClick={handlePrevPage}
                            className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                ${currentPage === 1 ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700' : 'text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                            `}
                            disabled={currentPage === 1}
                        >
                            {"<"}
                        </button>

                        {/* Page numbers */}
                        {pages.length > 5 ? (
                            <>
                                {/* Always show first page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                        ${1 === currentPage
                                        ? 'text-white border-primary bg-primary font-medium'
                                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                    `}
                                >
                                    1
                                </button>

                                {/* Show ellipsis if not at start */}
                                {currentPage > 3 && <span className="px-1 text-gray-400">...</span>}

                                {/* Show current and surrounding pages */}
                                {pages
                                    .filter(page => Math.abs(page - currentPage) < 2 && page !== 1 && page !== totalPages)
                                    .map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`hidden sm:flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                                ${page === currentPage
                                                ? 'text-white border-primary bg-primary font-medium'
                                                : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                            `}
                                        >
                                            {page}
                                        </button>
                                    ))
                                }

                                {/* Show ellipsis if not at end */}
                                {currentPage < totalPages - 2 &&
                                    <span className="px-1 text-gray-400">...</span>}

                                {/* Always show last page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                        ${totalPages === currentPage
                                        ? 'text-white border-primary bg-primary font-medium'
                                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                    `}
                                >
                                    {totalPages}
                                </button>
                            </>
                        ) : (
                            // Show all pages if 5 or fewer
                            pages.map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                        ${page === currentPage
                                        ? 'text-white border-primary bg-primary font-medium'
                                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                    `}
                                >
                                    {page}
                                </button>
                            ))
                        )}

                        {/* Next page button */}
                        <button
                            onClick={handleNextPage}
                            className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium transition-colors
                                ${currentPage === totalPages ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700' : 'text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                            `}
                            disabled={currentPage === totalPages}
                        >
                            {">"}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="card shadow-xl p-6 max-w-md w-full m-4 selection-bar-appear">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Confirmation de suppression</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Êtes-vous sûr de vouloir
                            supprimer {selectedItems.length} marque{selectedItems.length > 1 ? 's' : ''} ?
                            Cette action est irréversible.
                        </p>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button className="btn-ghost" onClick={cancelDelete}>
                                Annuler
                            </button>
                            <button className="btn-danger" onClick={confirmDelete}>
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default BrandsList;
